import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Btn } from "@/components/ui/Btn/Btn";
import { Icon } from "@/components/ui/Icon/Icon";
import { Textarea } from "@/components/ui/Textarea/Textarea";
import { toPlatformKey, type PlatformKey } from "@/components/ui/PlatformBadge/PlatformBadge";
import { Field } from "@/components/auth/FormShell";
import { FeedbackControls } from "@/components/feedback/FeedbackControls";
import { useIsMobile } from "@/lib/useMediaQuery";
import {
  mediaId,
  useAssembleDraft,
  useGeneratePostCandidates,
  useMedia,
  usePostCandidates,
  type PostCandidateBatch,
  type PostCandidateCaption,
} from "@/lib/queries";
import { MEDIA_MODELS } from "@notamen/core/media-models";
import type { MediaAspect } from "@notamen/core/types";
import shared from "./GenerateStudio.module.css";
import styles from "./GeneratePostPanel.module.css";

/** Where the post will run. Each placement sets the frame (aspect) and feeds a
 *  composition hint to the AI shot director (`postType`). TikTok has a single
 *  vertical video feed — no Feed/Story/Reel split — so it's one honest option.
 *  Replicated from GenerateMediaPanel so this panel is self-contained. */
type Placement = { id: string; label: string; aspect: MediaAspect };
// Keyed by PlatformKey ("ig"/"fb"/"tt") to match toPlatformKey() — the typed
// Record makes a key mismatch a compile error. The ids stay long (they carry
// the story/reel/feed/video substring the shot director keys its composition on).
const PLACEMENTS: Record<PlatformKey, Placement[]> = {
  ig: [
    { id: "instagram_feed", label: "Feed", aspect: "4:5" },
    { id: "instagram_story", label: "Story", aspect: "9:16" },
    { id: "instagram_reel", label: "Reel", aspect: "9:16" },
  ],
  fb: [
    { id: "facebook_feed", label: "Feed", aspect: "1:1" },
    { id: "facebook_story", label: "Story", aspect: "9:16" },
    { id: "facebook_reel", label: "Reel", aspect: "9:16" },
  ],
  tt: [{ id: "tiktok_video", label: "Video", aspect: "9:16" }],
};

// The studio platform options, long names ("instagram"/…) the endpoint
// validates against. Capitalized for display via CSS text-transform.
const PLATFORM_OPTIONS: { value: "instagram" | "facebook" | "tiktok"; label: string }[] = [
  { value: "instagram", label: "Instagram" },
  { value: "facebook", label: "Facebook" },
  { value: "tiktok", label: "TikTok" },
];

// Image models only (the post studio always pairs captions with a generated
// image). Default to the first bedrock image model ("stable-image").
const IMAGE_MODELS = MEDIA_MODELS.filter((m) => m.capability === "image");

const COUNTS = [1, 2, 3, 4, 5, 6];

/** A selection that any caption/image across any batch can set. Carries the
 *  source batch's assemble inputs so "Create draft" uses the right batch. */
interface PostSelection {
  batchId: string;
  platform: string;
  aspect?: MediaAspect;
  caption?: PostCandidateCaption;
  captionIdx?: number;
  mediaId?: string;
}

/**
 * Whole-post mode of the Generate Studio: one brief drives BOTH the caption
 * candidates and the image candidates. The user generates a batch, picks one
 * caption + one image (from the active batch or from a retained history
 * batch), then assembles a draft that lands in the approval queue.
 *
 * Honesty contract:
 * - Image tiles render their REAL job status (queued/running → spinner,
 *   failed → the worker's error, done → the resolved presigned image). No
 *   placeholder "feature coming" strings.
 * - The candidate list live-polls via usePostCandidates() (2s while any image
 *   job is queued/running) rather than the mutation return, so tiles flip from
 *   spinner to image without a manual refresh.
 * - A 402 payment_required from the server surfaces verbatim (budget message).
 */
export function GeneratePostPanel({
  ws,
  brand,
}: {
  ws: string;
  brand: string;
}) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const candidatesQuery = usePostCandidates(ws, brand);
  const mediaQuery = useMedia(ws, brand);
  const generate = useGeneratePostCandidates(ws, brand);
  const assemble = useAssembleDraft(ws, brand);

  const [platform, setPlatform] = useState<"instagram" | "facebook" | "tiktok">("instagram");
  const [modelId, setModelId] = useState<string>(IMAGE_MODELS[0]?.id ?? "stable-image");
  const [count, setCount] = useState<number>(4);
  const [brief, setBrief] = useState("");
  // Placement (postType) + frame (aspect). Defaults to the platform's first
  // placement; selecting a chip updates both together.
  const pkey = toPlatformKey(platform);
  const placements = PLACEMENTS[pkey] ?? [];
  const [placementId, setPlacementId] = useState<string>(
    () => PLACEMENTS[toPlatformKey("instagram")]?.[0]?.id ?? "",
  );
  const activePlacement =
    placements.find((p) => p.id === placementId) ?? placements[0];
  const aspect = activePlacement?.aspect ?? "4:5";

  const [selection, setSelection] = useState<PostSelection | null>(null);

  /** Switch platform → snap to its first placement so postType + aspect agree. */
  function onPlatformChange(next: "instagram" | "facebook" | "tiktok") {
    setPlatform(next);
    const first = PLACEMENTS[toPlatformKey(next)]?.[0];
    if (first) setPlacementId(first.id);
  }

  const batches = candidatesQuery.data?.batches ?? [];
  const conflicts = candidatesQuery.data?.conflicts ?? [];
  const activeBatch: PostCandidateBatch | undefined = batches[0];
  const historyBatches = batches.slice(1);

  // Resolve a mediaId → presigned URL from the media list (the worker writes
  // generated images as MediaRecords; the candidates endpoint resolves image
  // jobs to mediaIds). Built once per media-list change.
  const urlByMediaId = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of mediaQuery.data?.media ?? []) {
      if (m.url) map.set(mediaId(m), m.url);
    }
    return map;
  }, [mediaQuery.data]);

  // The candidate poll surfaces image mediaIds as jobs finish, but the URLs for
  // those freshly-written generated MediaRecords live in the SEPARATE media
  // list (post-candidates returns no URLs). Refresh that list once whenever new
  // mediaIds appear so done tiles resolve to a real image instead of sticking
  // on "Finalizing…" (mirrors GenerateMediaPanel's completion refresh).
  const resolvedMediaKey = batches
    .flatMap((b) => b.images.flatMap((im) => im.mediaIds))
    .join(",");
  useEffect(() => {
    if (resolvedMediaKey) {
      void qc.invalidateQueries({ queryKey: ["media", ws, brand] });
    }
  }, [resolvedMediaKey, qc, ws, brand]);

  const canGenerate = !!brief.trim() && !generate.isPending;

  async function onGenerate() {
    if (!canGenerate) return;
    await generate.mutateAsync({
      brief: brief.trim(),
      platform,
      count,
      modelId,
      shotMode: "scene",
      aspect,
      postType: placementId,
    });
    // Clear any stale selection so it never points at a retired batch's image.
    setSelection(null);
  }

  const canAssemble =
    !!selection?.caption && !!selection?.mediaId && !assemble.isPending;

  async function onCreateDraft() {
    if (!selection?.caption || !selection.mediaId || assemble.isPending) return;
    await assemble.mutateAsync({
      batchId: selection.batchId,
      platform: selection.platform,
      caption: selection.caption,
      mediaIds: [selection.mediaId],
      ...(selection.aspect ? { aspect: selection.aspect } : {}),
      mediaType: "image",
    });
    navigate(`/w/${ws}/b/${brand}/queue`);
  }

  function selectCaption(batch: PostCandidateBatch, caption: PostCandidateCaption, idx: number) {
    setSelection((s) => ({
      batchId: batch.batchId,
      platform: batch.platform,
      ...(batch.aspect ? { aspect: batch.aspect } : {}),
      caption,
      captionIdx: idx,
      // Keep the image only if it came from the same batch; otherwise clear it
      // so a draft never mixes a caption and image from different batches.
      ...(s && s.batchId === batch.batchId && s.mediaId ? { mediaId: s.mediaId } : {}),
    }));
  }

  function selectImage(batch: PostCandidateBatch, mid: string) {
    setSelection((s) => ({
      batchId: batch.batchId,
      platform: batch.platform,
      ...(batch.aspect ? { aspect: batch.aspect } : {}),
      mediaId: mid,
      ...(s && s.batchId === batch.batchId && s.caption
        ? { caption: s.caption, captionIdx: s.captionIdx }
        : {}),
    }));
  }

  return (
    <div className={shared.layout}>
      {/* Request form */}
      <div className={shared.form}>
        <Field label="Platform">
          <div className={shared.chips}>
            {PLATFORM_OPTIONS.map((p) => (
              <button
                key={p.value}
                type="button"
                className={`${shared.chip} ${platform === p.value ? shared.chipOn : ""}`}
                onClick={() => onPlatformChange(p.value)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </Field>

        {placements.length > 0 && (
          <Field
            label="Post type"
            hint="Sets the frame and tailors the shot to where it’ll run."
          >
            <div className={shared.chips}>
              {placements.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`${shared.chip} ${placementId === p.id ? shared.chipOn : ""}`}
                  onClick={() => setPlacementId(p.id)}
                >
                  {p.label} · {p.aspect}
                </button>
              ))}
            </div>
          </Field>
        )}

        <Field label="How many options" hint="Generates this many caption + image pairs to pick from.">
          <div className={styles.countRow}>
            {COUNTS.map((n) => (
              <button
                key={n}
                type="button"
                className={`${shared.chip} ${count === n ? shared.chipOn : ""}`}
                onClick={() => setCount(n)}
              >
                {n}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Image model" htmlFor="gp-model" hint="The engine that renders the post images.">
          <select
            id="gp-model"
            className={styles.select}
            value={modelId}
            onChange={(e) => setModelId(e.target.value)}
          >
            {IMAGE_MODELS.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label} · {m.usageUnits} unit{m.usageUnits === 1 ? "" : "s"}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Brief" htmlFor="gp-brief" hint="One brief drives BOTH the captions and the images.">
          <Textarea
            id="gp-brief"
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            placeholder="What's the post about? A product drop, a sale, a behind-the-scenes moment…"
            rows={4}
          />
        </Field>

        {generate.error && <p className={shared.error}>{generate.error.message}</p>}

        <Btn
          variant="primary"
          full
          size="lg"
          onClick={() => void onGenerate()}
          disabled={!canGenerate}
          loading={generate.isPending}
          iconRight={<Icon name="zap" size={14} />}
        >
          {generate.isPending ? "Generating…" : "Generate post"}
        </Btn>
      </div>

      {/* Results */}
      <div className={shared.results}>
        {conflicts.length > 0 && (
          <div className={styles.conflict} role="alert">
            <Icon name="alert" size={16} />
            <div>
              <strong>Your feedback is fighting your brand brain</strong>
              <ul>
                {conflicts.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
        {candidatesQuery.isError && (
          <div className={shared.failed}>
            <Icon name="info" size={16} color="var(--danger)" />
            <div>
              <strong>Couldn’t load your generations.</strong>
              <p>{candidatesQuery.error?.message ?? "Try again in a moment."}</p>
            </div>
          </div>
        )}

        {!candidatesQuery.isError && batches.length === 0 && (
          <div className={shared.placeholder}>
            <Icon name="zap" size={28} color="var(--text-4)" />
            <p>No generations yet — write a brief and generate your first post.</p>
          </div>
        )}

        {activeBatch && (
          <section className={styles.pickGroup}>
            <div>
              <h2 className={styles.sectionHead}>Pick your post</h2>
              <p className={styles.sectionHint}>
                Choose one caption and one image. They become a draft you can review,
                edit, and schedule from the queue.
              </p>
            </div>

            <CaptionPicker
              ws={ws}
              brand={brand}
              batch={activeBatch}
              selectedIdx={selection?.batchId === activeBatch.batchId ? selection.captionIdx : undefined}
              onSelect={selectCaption}
            />

            <ImagePicker
              ws={ws}
              brand={brand}
              batch={activeBatch}
              urlByMediaId={urlByMediaId}
              selectedMediaId={selection?.batchId === activeBatch.batchId ? selection.mediaId : undefined}
              onSelect={selectImage}
              isMobile={isMobile}
            />

            <div className={styles.assembleBar}>
              {assemble.error && <p className={shared.error}>{assemble.error.message}</p>}
              <p className={styles.assembleHint}>
                {selection?.caption && selection?.mediaId
                  ? "Caption and image selected — create the draft to send it to your queue."
                  : "Select one caption and one image to enable Create draft."}
              </p>
              <Btn
                variant="primary"
                size="lg"
                onClick={() => void onCreateDraft()}
                disabled={!canAssemble}
                loading={assemble.isPending}
                iconRight={<Icon name="check" size={14} />}
              >
                {assemble.isPending ? "Creating draft…" : "Create draft"}
              </Btn>
            </div>
          </section>
        )}

        {historyBatches.length > 0 && (
          <div className={styles.history}>
            <div>
              <h2 className={styles.sectionHead}>Generation history</h2>
              <p className={styles.sectionHint}>
                Unpicked options stay available for your next 2 generations, then they
                expire. The generated images remain in your media library. You can still
                pick a caption and image from any generation below.
              </p>
            </div>
            {historyBatches.map((batch) => (
              <article key={batch.batchId} className={styles.historyBatch}>
                <div className={styles.historyMeta}>
                  <span className={styles.historyWhen}>{shortWhen(batch.createdAt)}</span>
                  <span className={styles.pickGroupLabel}>{batch.platform}</span>
                </div>
                <p className={styles.historyBrief}>{snippet(batch.brief)}</p>
                <CaptionPicker
                  ws={ws}
                  brand={brand}
                  batch={batch}
                  selectedIdx={selection?.batchId === batch.batchId ? selection.captionIdx : undefined}
                  onSelect={selectCaption}
                />
                <ImagePicker
                  ws={ws}
                  brand={brand}
                  batch={batch}
                  urlByMediaId={urlByMediaId}
                  selectedMediaId={selection?.batchId === batch.batchId ? selection.mediaId : undefined}
                  onSelect={selectImage}
                  isMobile={isMobile}
                />
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Caption candidate cards. One selectable at a time across the whole panel;
 *  `selectedIdx` is set only when the active selection came from THIS batch. */
function CaptionPicker({
  ws,
  brand,
  batch,
  selectedIdx,
  onSelect,
}: {
  ws: string;
  brand: string;
  batch: PostCandidateBatch;
  selectedIdx: number | undefined;
  onSelect: (batch: PostCandidateBatch, caption: PostCandidateCaption, idx: number) => void;
}) {
  if (batch.captions.length === 0) return null;
  return (
    <div>
      <span className={styles.pickGroupLabel}>Captions</span>
      <div className={styles.captionList}>
        {batch.captions.map((c, idx) => {
          const on = selectedIdx === idx;
          return (
            <div key={idx} className={styles.captionItem}>
              <button
                type="button"
                className={`${styles.captionCard} ${on ? styles.captionCardOn : ""}`}
                onClick={() => onSelect(batch, c, idx)}
                aria-pressed={on}
              >
                {c.hook && <p className={styles.captionHook}>{c.hook}</p>}
                <p className={styles.captionBody}>{c.body}</p>
                {c.hashtags && c.hashtags.length > 0 && (
                  <p className={styles.captionTags}>
                    {c.hashtags.map((h) => (h.startsWith("#") ? h : `#${h}`)).join(" ")}
                  </p>
                )}
                {c.callToAction && <p className={styles.captionCta}>{c.callToAction}</p>}
              </button>
              <FeedbackControls
                ws={ws}
                brand={brand}
                target="caption"
                source="studio"
                context={{
                  batchId: batch.batchId,
                  captionIdx: idx,
                  platform: batch.platform,
                  ...(batch.postType ? { postType: batch.postType } : {}),
                  captionText: c.body,
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Image candidate tiles. Each image carries its REAL job status: queued/
 *  running → spinner, failed → the worker's error, done → the resolved
 *  presigned image (first mediaId). Selecting a done tile selects its first
 *  mediaId. */
function ImagePicker({
  ws,
  brand,
  batch,
  urlByMediaId,
  selectedMediaId,
  onSelect,
  isMobile,
}: {
  ws: string;
  brand: string;
  batch: PostCandidateBatch;
  urlByMediaId: Map<string, string>;
  selectedMediaId: string | undefined;
  onSelect: (batch: PostCandidateBatch, mediaId: string) => void;
  isMobile: boolean;
}) {
  if (batch.images.length === 0) return null;
  return (
    <div>
      <span className={styles.pickGroupLabel}>Images</span>
      {/* 2-across on phone, 4-across on desktop (CSS @media handles the grid;
          this inline override keeps the count honest if CSS load order ever
          changes — phones get a denser 2-col layout). */}
      <div
        className={styles.imageGrid}
        style={isMobile ? { gridTemplateColumns: "repeat(2, 1fr)" } : undefined}
      >
        {batch.images.map((im) => {
          const pending = im.status === "queued" || im.status === "running";
          const failed = im.status === "failed";
          const mid = im.mediaIds[0];
          const url = mid ? urlByMediaId.get(mid) : undefined;
          const on = !!mid && selectedMediaId === mid;
          const selectable = !!mid && !!url && !pending && !failed;
          return (
            <div key={im.jobId} className={styles.imageItem}>
              <button
                type="button"
                className={`${styles.imageTile} ${on ? styles.imageTileOn : ""}`}
                onClick={() => selectable && mid && onSelect(batch, mid)}
                disabled={!selectable}
                aria-pressed={on}
              >
                {pending ? (
                  <span className={styles.tileState}>
                    <span className={shared.spinner} aria-hidden="true" />
                    Generating…
                  </span>
                ) : failed ? (
                  <span className={`${styles.tileState} ${styles.tileError}`}>
                    <Icon name="info" size={14} />
                    {im.error ?? "Image failed"}
                  </span>
                ) : url ? (
                  <img className={styles.imageThumb} src={url} alt="Generated post image" loading="lazy" />
                ) : (
                  <span className={styles.tileState}>
                    <span className={shared.spinner} aria-hidden="true" />
                    Finalizing…
                  </span>
                )}
                {on && (
                  <span className={styles.tileCheck}>
                    <Icon name="check" size={12} />
                  </span>
                )}
              </button>
              {selectable && mid && (
                <FeedbackControls
                  ws={ws}
                  brand={brand}
                  target="image"
                  source="studio"
                  context={{
                    batchId: batch.batchId,
                    mediaId: mid,
                    platform: batch.platform,
                    ...(batch.postType ? { postType: batch.postType } : {}),
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Short relative time for a generation's createdAt (ISO string). */
function shortWhen(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffMs = Date.now() - then;
  const min = Math.round(diffMs / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  return `${day}d ago`;
}

/** First ~120 chars of a brief for the history list. */
function snippet(text: string): string {
  const t = text.trim();
  return t.length > 120 ? `${t.slice(0, 117)}…` : t;
}
